import {useTranslation} from 'react-i18next';
import React from 'react';
import {type TypedTranslation} from '../../../../i18n';
import {IValidationErrorObject} from '../../../../incase-domain/common/validationErrorResponse';
import ElementShadow from '../common/elementShadow';
import {TABS} from '../../form/part/tabs';
import {IElementChatbot} from '../../../../incase-domain/elements/chatbot';
import {getErrorClasses} from '../../../../utils/validation';
import {useHighlightedElement, useSetHighlightedElement} from '../../../../utils/hooks/slideUpdates/useSetSlideElement';
import AnimationForm from '../common/animationForm';

interface HighlightedChatbotElementComponentProps {
    errors: IValidationErrorObject | null;
    activeTab: string;
}

export default function HighlightedChatbotElementComponent(props: HighlightedChatbotElementComponentProps): JSX.Element {
    const {errors, activeTab} = props;
    const {t} = useTranslation();
    const highlightedElement = useHighlightedElement() as IElementChatbot;
    const updateHighlightedElement = useSetHighlightedElement();

    if (activeTab === TABS.ADVANCED.id) {
        return (
            <>
                <ElementShadow highlightedElement={highlightedElement} highlightedElementChangeHandler={updateHighlightedElement} />
                <AnimationForm
                    appearAfter={highlightedElement.appearAfter || 0}
                    elementAnimations={highlightedElement.elementAnimations}
                    highlightedElementChangeHandler={updateHighlightedElement}
                />
            </>
        );
    }

    return (
        <div className="l-group--form u-mt-1 c-calculatorSidepanel">
            <div className={`l-group__item ${getErrorClasses(errors, 'header')}`}>
                <label className="c-label">{t('presEditPage.editSidebar.elements.chatbot.header')}</label>
                <div className="c-sidepanel__input">
                    <input
                        type="text"
                        className="c-sidepanel__input_field"
                        value={highlightedElement.header}
                        onChange={(e) => updateHighlightedElement.single('header', e.target.value.trim())}
                    />
                </div>
                {errors?.header && <div className="c-error">{t(errors.header as TypedTranslation)}</div>}
            </div>

            <div className={`l-group__item ${getErrorClasses(errors, 'profile')}`}>
                <label className="c-label">{t('presEditPage.editSidebar.elements.chatbot.profile')}</label>
                <div className="c-sidepanel__input">
                    <textarea
                        rows={4}
                        className="c-sidepanel__input_field"
                        value={highlightedElement.profile}
                        onChange={(e) => updateHighlightedElement.single('profile', e.target.value.trim())}
                    />
                </div>
                {errors?.profile && <div className="c-error">{t(errors.profile as TypedTranslation)}</div>}
            </div>

            <div className={`l-group__item ${getErrorClasses(errors, 'therapy')}`}>
                <label className="c-label">{t('presEditPage.editSidebar.elements.chatbot.therapy')}</label>
                <div className="c-sidepanel__input">
                    <textarea
                        rows={4}
                        className="c-sidepanel__input_field"
                        value={highlightedElement.therapy}
                        onChange={(e) => updateHighlightedElement.single('therapy', e.target.value.trim())}
                    />
                </div>
                {errors?.therapy && <div className="c-error">{t(errors.therapy as TypedTranslation)}</div>}
            </div>

            <div className={`l-group__item ${getErrorClasses(errors, 'diagnosis')}`}>
                <label className="c-label">{t('presEditPage.editSidebar.elements.chatbot.diagnosis')}</label>
                <div className="c-sidepanel__input">
                    <textarea
                        rows={4}
                        className="c-sidepanel__input_field"
                        value={highlightedElement.diagnosis}
                        onChange={(e) => updateHighlightedElement.single('diagnosis', e.target.value.trim())}
                    />
                </div>
                {errors?.diagnosis && <div className="c-error">{t(errors.diagnosis as TypedTranslation)}</div>}
            </div>
        </div>
    );
}
